import React, { useState, useRef } from "react";
import Button from "react-bootstrap/Button";
import { MdModeEdit } from "react-icons/md";
import { sendIdentityServiceAuthenticatedRequest } from "services/AsyncRequestService";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Formik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import AdminPopUpDismissableModal from "components/adminPopupDismissableModal";
import { getErrorMessage } from "services/UtilityService";
import { showNotification } from "services/NotificationService";
import "assets/css/user.css";
import { customMultiSelectStyles } from 'commons/styles/CustomStyles';

const EditUser = ({ user, roles, handler, showButton: initialShowButton }) => {
  const [showButton, setShowButton] = useState(initialShowButton);
  const modalRef = useRef(null);

  const editUser = (values, setSubmitting, closeModalHandle) => {
    const valuesDto = {
      email: values.email,
      name: values.userName,
      ipAddress: values.ipAddress,
      roles: values.roles,
    };
    sendIdentityServiceAuthenticatedRequest(
      '/users',
      'PUT',
      valuesDto,
      (r) => {
        setSubmitting(false);
        showNotification({
          title: 'Action successful!',
          message: 'User edited successfully.',
          position: 'tc',
          type: 'success',
        });
        closeModalHandle();
        handler();
      },
      (r) => {
        setSubmitting(false);
        showNotification({
          title: 'Error!',
          message: getErrorMessage(r.data) || getErrorMessage('UNABLE_TO_EDIT_USER'),
          position: 'tc',
          type: 'error',
        });
      }
    );
  };

  const onMultiSelectChange = (options, setFieldValue) => {
    const tempRolesList = options.map((x) => x.key);
    setFieldValue('roles', tempRolesList);
  };

  const invertShowbutton = () => {
    setShowButton(!showButton);
  };

  const userRolesList = user.rolesList
    ? user.rolesList.split(", ").map((role) => ({ key: role, value: role }))
    : [];

  const rolesOptions = roles
    ? roles.map((c) => ({
        value: c.name,
        key: c.name,
      }))
    : [];

  const editUserFormSchema = Yup.object().shape({
    userName: Yup.string()
      .max(60, 'Maximum Length: ' + 60)
      .required('username is required'),
    email: Yup.string().email().required('email is required'),
    roles: Yup.array().min(1, 'Atleast select 1 role'),
    ipAddress: Yup.string()
      .nullable()
      .matches(
        /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/i,
        'Not a valid IP address'
      ),
  });

  if (showButton) {
    return (
      <OverlayTrigger overlay={<Tooltip>Edit User</Tooltip>}>
        <MdModeEdit
          size={20}
          className="user-action-button edit-user"
          onClick={invertShowbutton}
        ></MdModeEdit>
      </OverlayTrigger>
    );
  } else {
    return (
      <>
        <OverlayTrigger overlay={<Tooltip>Edit User</Tooltip>}>
          <MdModeEdit
            className="user-action-button edit-user"
            size={20}
            onClick={invertShowbutton}
          ></MdModeEdit>
        </OverlayTrigger>
        <AdminPopUpDismissableModal
          ref={modalRef}
          openModalButtonText="Edit User"
          openModalButtonStyle="info"
          openModalButtonBlock={true}
          openModalButtonDisabled={false}
          openModalBsuttonId="edit-user-role-btn-popup"
          preventCloseWhenClickedOutsideModal={true}
          initialStateOpen={true}
          hideFooter={true}
          onClose={invertShowbutton}
          title={'Edit user details'}
          showIcons={true}
        >
          <Formik
            initialValues={{
              ...user,
              roles: userRolesList.map((role) => role.key),
            }}
            onSubmit={(values, { setSubmitting }) => {
              editUser(values, setSubmitting, () =>
                modalRef.current.handleClose()
              );
            }}
            validationSchema={editUserFormSchema}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.email}
                        onChange={handleChange}
                        name="email"
                        onBlur={handleBlur}
                        readOnly
                      ></Form.Control>
                      <Form.Text className="text-small text-danger">
                        {errors.name && touched.name && errors.name}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.userName}
                        onChange={handleChange}
                        name="userName"
                        onBlur={handleBlur}
                      ></Form.Control>
                      <Form.Text className="text-small text-danger">
                        {errors.name && touched.name && errors.name}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>IP Address</Form.Label>
                      <Form.Control
                        type="text"
                        value={values.ipAddress}
                        onChange={handleChange}
                        name="ipAddress"
                        onBlur={handleBlur}
                      ></Form.Control>
                      <Form.Text className="text-small text-danger">
                        {errors.ipAddress &&
                          touched.ipAddress &&
                          errors.ipAddress}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group>
                      <Form.Label>Roles</Form.Label>
                      <Select
                        isMulti
                        placeholder="Select Role"
                        options={rolesOptions}
                        name="roles"
                        isClearable={false}
                        value={rolesOptions.filter((opt) =>
                          values.roles.includes(opt.key)
                        )}
                        onChange={(selected) =>
                          onMultiSelectChange(selected || [], setFieldValue)
                        }
                        getOptionLabel={(opt) => opt.value}
                        getOptionValue={(opt) => opt.key}
                        styles={{
                          ...customMultiSelectStyles,
                        }}
                      />
                      <Form.Text className="text-small text-danger">
                        {errors.roles && touched.roles && errors.roles}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="bottom-spacer top-spacer">
                  <Col md={{ offset: "4", span: "4" }}>
                    <Button
                      variant="outline-dark"
                      block
                      onClick={() => modalRef.current.handleClose()}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                  </Col>
                  <Col md={4}>
                    <Button
                      type="submit"
                      variant="secondary"
                      className="btn-text-white"
                      block
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Editing... ' : 'Edit'}
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </AdminPopUpDismissableModal>
      </>
    );
  }
};

export default EditUser;
